import React, { useState } from 'react';
import { XCircle, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { DeleteRequest } from '../types';
import { LoadingSpinner } from './LoadingSpinner';

interface BulkRejectDeleteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRequests: DeleteRequest[];
  onConfirm: (reason: string) => Promise<void>;
}

export const BulkRejectDeleteRequestModal: React.FC<BulkRejectDeleteRequestModalProps> = ({
  isOpen,
  onClose,
  selectedRequests,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (reason.trim().length < 10) {
      setError('Rejection reason must be at least 10 characters long.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onConfirm(reason);
      setReason('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason('');
      setError(null);
      onClose();
    }
  };

  const remainingChars = 10 - reason.trim().length;
  const isValidReason = reason.trim().length >= 10;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Reject Delete Requests">
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900 mb-1">Rejection Notice</h4>
            <p className="text-sm text-yellow-700">
              You are about to reject {selectedRequests.length} delete{' '}
              {selectedRequests.length === 1 ? 'request' : 'requests'}. The accounts will remain active.
            </p>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-2">
            Selected Requests ({selectedRequests.length})
          </h4>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            {selectedRequests.map((request) => (
              <div
                key={request.requestId}
                className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{request.name}</p>
                    <p className="text-sm text-gray-600">{request.email}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {request.accountType === 'user' ? 'Service User' : 'Provider'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Reason: {request.reason}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-2">
            Rejection Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            id="rejection-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="Enter a detailed reason for rejecting these delete requests..."
            disabled={isLoading}
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">
              {reason.length} characters
              {!isValidReason && remainingChars > 0 && (
                <span className="text-orange-600 ml-1">
                  ({remainingChars} more needed)
                </span>
              )}
            </p>
            {isValidReason && (
              <p className="text-xs text-green-600 font-medium">
                Valid reason provided
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleClose}
            disabled={isLoading}
            className="bg-gray-500 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !isValidReason}
            className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                Processing...
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4" />
                Reject All ({selectedRequests.length})
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
